<template>
  <span>
    <el-input
      v-if="isEditing"
      ref="editInput"
      v-model="tempValue"
      size="mini"
      class="value-input-edit"
      @blur="applyValue"
      @keydown.native="handleKeydown"
    />
    <span 
      v-else 
      class="value-pill editable-value" 
      @click="startEdit"
      @keydown.enter.prevent="startEdit"
      @keydown.space.prevent="startEdit"
      role="button"
      tabindex="0"
      title="Click to edit"
    >
      <span class="value-text">{{ displayValue }}</span>
      <i class="el-icon-edit edit-icon" aria-hidden="true"></i>
    </span>
  </span>
</template>

<script>
export default {
  name: 'EditableValue',
  props: {
    value: {
      type: Number,
      required: true
    },
    formatter: {
      type: Function,
      default: (val) => String(val)
    },
    min: {
      type: Number,
      default: null
    },
    max: {
      type: Number,
      default: null
    },
    validator: {
      type: Function,
      default: null
    },
    errorMessage: {
      type: String,
      default: 'Invalid value'
    },
    // If true, values > 1 will be divided by 100 (for percentage inputs)
    acceptPercentage: {
      type: Boolean,
      default: false
    },
    // Round to integer
    integer: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isEditing: false,
      tempValue: '',
      isApplying: false
    }
  },
  computed: {
    displayValue() {
      return this.formatter(this.value)
    }
  },
  methods: {
    startEdit() {
      this.isEditing = true
      this.tempValue = String(this.value)
      this.$nextTick(() => {
        if (this.$refs.editInput && this.$refs.editInput.$el) {
          const input = this.$refs.editInput.$el.querySelector('input')
          if (input) {
            input.focus()
            input.select()
          }
        }
      })
    },
    cancelEdit() {
      this.isEditing = false
      this.tempValue = ''
      this.isApplying = false
    },
    applyValue() {
      // Prevent double execution (from both blur and Enter key)
      if (this.isApplying || !this.isEditing) {
        return
      }
      this.isApplying = true
      
      let newValue = parseFloat(this.tempValue)
      
      if (isNaN(newValue)) {
        this.$notify({
          title: 'Error',
          message: 'Please enter a valid number',
          type: 'error',
          position: 'top-right'
        })
        this.cancelEdit()
        return
      }

      // Handle percentage conversion
      if (this.acceptPercentage && newValue > 1) {
        newValue = newValue / 100
      }

      // Apply integer rounding
      if (this.integer) {
        newValue = Math.round(newValue)
      }

      // Validate min/max
      if (this.min !== null && newValue < this.min) {
        this.$notify({
          title: 'Error',
          message: this.errorMessage,
          type: 'error',
          position: 'top-right'
        })
        this.cancelEdit()
        return
      }
      if (this.max !== null && newValue > this.max) {
        this.$notify({
          title: 'Error',
          message: this.errorMessage,
          type: 'error',
          position: 'top-right'
        })
        this.cancelEdit()
        return
      }

      // Custom validator
      if (this.validator && !this.validator(newValue)) {
        this.$notify({
          title: 'Error',
          message: this.errorMessage,
          type: 'error',
          position: 'top-right'
        })
        this.cancelEdit()
        return
      }

      this.$emit('change', newValue)
      this.cancelEdit()
    },
    handleKeydown(e) {
      if (e.key === 'Enter') {
        this.applyValue()
      } else if (e.key === 'Escape') {
        this.cancelEdit()
      }
    }
  }
}
</script>

<style scoped>
/* Inline value badge next to labels */
.value-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  padding: 2px 8px;
  font-size: 14px;
  line-height: 1;
  border-radius: 10px;
  background: #dbeafe;
  color: #1e40af;
  vertical-align: middle;
  border: 1px solid #93c5fd;
}

/* Editable value pill styles */
.value-pill.editable-value {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.value-pill.editable-value:hover {
  background: #dbeafe;
  border-color: #3b82f6;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.15);
}

.value-pill.editable-value:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.value-pill.editable-value:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(59, 130, 246, 0.15);
}

.edit-icon {
  font-size: 12px;
  opacity: 0.6;
}

.value-pill.editable-value:hover .edit-icon,
.value-pill.editable-value:focus .edit-icon {
  opacity: 0.9;
}

/* Edit input style */
.value-input-edit {
  display: inline-block;
  width: 80px;
  margin-left: 6px;
  vertical-align: middle;
}

.value-input-edit >>> .el-input__inner {
  padding: 2px 8px;
  height: 24px;
  line-height: 24px;
  font-size: 12px;
  text-align: center;
  border: 2px solid #3b82f6;
  border-radius: 10px;
  background: #ffffff;
  color: #1d4ed8;
  font-weight: 500;
}

.value-input-edit >>> .el-input__inner:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}
</style>

